/* eslint-disable sonarjs/no-duplicate-string */
import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import {
    SelectionSourceScreen,
    SelectionSourceType,
} from '@superbet-group/web.types';
import Vue from 'vue';
import {
    ActionContext,
    MutationTree,
} from 'vuex';

import { logger } from '@app/logger';
import { IState } from '@app/store';
import SportSelection from '@models/betSlip/SportSelection';
import { betBuilderService } from '@sportOfferFederation/bet-builder/services/betBuilder';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';

import {
    BetBuilderState,
    Selection,
} from './types';
import { BetBuilderOdd } from '../types/BetBuilderOdd';
import { betBuilderAnalyticsStore } from './analytics';
import { ensureMinimumDurationForSlowProcess } from '../utils/async';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { BetBuilderFeatureFlags } from '../feature-flags/BetBuilderFeatureFlags';
import OfferEvent from '@core/models/offer/OfferEvent';
import { BetBuilderMarket } from '../types/BetBuilderMarket';
import { BetBuilderPreviewSummary } from '../types/BetBuilderPreview';
import { betBuilderHistoryService } from '../services/betBuilderHistoryService';
import BetSlipOdd from '@core/models/base/BetSlipOdd';
import { AuthenticationSubscriptionService } from '@store/modules/data/authentication/services/authenticationSubscriptionService';
import { toggleSelectionInOdds } from '../utils/toggleSelectionInOdds';
import { createOddValueDynamics } from '../utils/createOddValueDynamics';
import { createOfferEventGetter } from '../utils/createOfferEventGetter';
import { keepActiveOdds } from '../utils/keepActiveOdds';
import { getSummaryAsSuperbet } from '../utils/getSummaryAsSuperbet';
import { getInactiveSelectedOddsCount } from '../utils/getInactiveSelectedOddsCount';
import { isOddsSelectionSummaryInactive } from '../utils/isOddsSelectionSummaryInactive';

const showLoaderAfterPreviewDurationMs = 100;
const minimumPreviewLoadingStateDurationMs = 300;

function getDefaultState(): BetBuilderState {
    return {
        isBetBuilderLoading: false,
        isBetCreateLoading: false,
        isPreviewLoading: false,
        isAvailable: false,
        selectedOdds: [],
        eventId: null,
        markets: [],
        summary: null,
        unavailableOddUuids: [],
        oddValueDynamics: {},
        isShowingError: false,
        isFeatureEnabled: false,
        isEventCardLabelEnabled: false,
        isOnboardingEnabled: false,
        isCrossSellingFromSuperbetsBannerEnabled: false,
        cachedBetslipOddNames: {},
    };
}

const mutationTypes = {
    SET_IS_BET_BUILDER_LOADING: 'SET_IS_BET_BUILDER_LOADING',
    SET_IS_BET_CREATE_LOADING: 'SET_IS_BET_CREATE_LOADING',
    SET_IS_PREVIEW_LOADING: 'SET_IS_PREVIEW_LOADING',
    SET_EVENT_ID: 'SET_EVENT_ID',
    SET_IS_AVAILABLE: 'SET_IS_AVAILABLE',
    SET_MARKETS: 'SET_MARKETS',
    TOGGLE_SELECTION: 'TOGGLE_SELECTION',
    SET_SELECTED_ODDS: 'SET_SELECTED_ODDS',
    CLEAR: 'CLEAR',
    SET_SUMMARY: 'SET_SUMMARY',
    SET_UNAVAILABLE_ODD_UUIDS: 'SET_UNAVAILABLE_ODD_UUIDS',
    SET_ODD_VALUE_DYNAMICS: 'SET_ODD_VALUE_DYNAMICS',
    SET_IS_SHOWING_ERROR: 'SET_IS_SHOWING_ERROR',
    SET_IS_FEATURE_ENABLED: 'SET_IS_FEATURE_ENABLED',
    SET_IS_EVENT_CARD_LABEL_ENABLED: 'SET_IS_EVENT_CARD_LABEL_ENABLED',
    SET_IS_ONBOARDING_ENABLED: 'SET_IS_ONBOARDING_ENABLED',
    SET_IS_CROSS_SELLING_FROM_SUPERBETS_BANNER_ENABLED: 'SET_IS_CROSS_SELLING_FROM_SUPERBETS_BANNER_ENABLED',
    SET_CACHED_BETSLIP_ODD_NAME: 'SET_CACHED_BETSLIP_ODD_NAME',
};

const mutations: MutationTree<BetBuilderState> = {
    [mutationTypes.SET_IS_FEATURE_ENABLED]: createSimpleMutator<BetBuilderState, 'isFeatureEnabled'>('isFeatureEnabled'),
    [mutationTypes.SET_IS_EVENT_CARD_LABEL_ENABLED]: createSimpleMutator<BetBuilderState, 'isEventCardLabelEnabled'>('isEventCardLabelEnabled'),
    [mutationTypes.SET_IS_ONBOARDING_ENABLED]: createSimpleMutator<BetBuilderState, 'isOnboardingEnabled'>('isOnboardingEnabled'),
    [mutationTypes.SET_IS_CROSS_SELLING_FROM_SUPERBETS_BANNER_ENABLED]: createSimpleMutator<BetBuilderState, 'isCrossSellingFromSuperbetsBannerEnabled'>('isCrossSellingFromSuperbetsBannerEnabled'),
    [mutationTypes.SET_MARKETS]: createSimpleMutator<BetBuilderState, 'markets'>('markets'),
    [mutationTypes.SET_IS_AVAILABLE]: createSimpleMutator<BetBuilderState, 'isAvailable'>(
        'isAvailable',
    ),
    [mutationTypes.SET_IS_BET_BUILDER_LOADING]: createSimpleMutator<BetBuilderState, 'isBetBuilderLoading'>('isBetBuilderLoading'),
    [mutationTypes.SET_IS_BET_CREATE_LOADING]: createSimpleMutator<BetBuilderState, 'isBetCreateLoading'>('isBetCreateLoading'),
    [mutationTypes.SET_IS_PREVIEW_LOADING]: createSimpleMutator<BetBuilderState, 'isPreviewLoading'>('isPreviewLoading'),
    [mutationTypes.SET_SELECTED_ODDS]: createSimpleMutator<BetBuilderState, 'selectedOdds'>(
        'selectedOdds',
    ),
    [mutationTypes.SET_EVENT_ID]: createSimpleMutator<BetBuilderState, 'eventId'>('eventId'),
    [mutationTypes.SET_SUMMARY]: createSimpleMutator<BetBuilderState, 'summary'>('summary'),
    [mutationTypes.SET_UNAVAILABLE_ODD_UUIDS]: createSimpleMutator<BetBuilderState, 'unavailableOddUuids'>('unavailableOddUuids'),
    [mutationTypes.SET_ODD_VALUE_DYNAMICS]: createSimpleMutator<BetBuilderState, 'oddValueDynamics'>('oddValueDynamics'),
    [mutationTypes.SET_IS_SHOWING_ERROR]: createSimpleMutator<BetBuilderState, 'isShowingError'>('isShowingError'),
    [mutationTypes.TOGGLE_SELECTION](state, selection: Selection) {
        const hasSelection = state.selectedOdds.includes(selection.uuid);
        if (hasSelection) {
            state.selectedOdds = state.selectedOdds.filter((uuid) => uuid !== selection.uuid);
        } else {
            state.selectedOdds.push(selection.uuid);
        }
    },
    [mutationTypes.SET_CACHED_BETSLIP_ODD_NAME](state, payload: { eventId: number; name: string }) {
        Vue.set(state.cachedBetslipOddNames, payload.eventId, payload.name);
    },
    [mutationTypes.CLEAR](state) {
        Object.assign(state, {
            ...getDefaultState(),
            isFeatureEnabled: state.isFeatureEnabled,
            isEventCardLabelEnabled: state.isEventCardLabelEnabled,
            isOnboardingEnabled: state.isOnboardingEnabled,
            isCrossSellingFromSuperbetsBannerEnabled: state.isCrossSellingFromSuperbetsBannerEnabled,
            cachedBetslipOddNames: state.cachedBetslipOddNames,
        });
    },
};

export const store = {
    namespaced: true,
    state: getDefaultState(),
    modules: {
        analytics: betBuilderAnalyticsStore,
    },
    getters: {
        isBetBuilderEnabled(state: BetBuilderState): boolean {
            return state.isFeatureEnabled;
        },
        isEventCardLabelEnabled(state: BetBuilderState): boolean {
            return state.isEventCardLabelEnabled;
        },
        isOnboardingEnabled(state: BetBuilderState): boolean {
            return state.isOnboardingEnabled;
        },
        isCrossSellingFromSuperbetsBannerEnabled(state: BetBuilderState): boolean {
            return state.isCrossSellingFromSuperbetsBannerEnabled;
        },
        selectionsCount(state: BetBuilderState): number {
            return state.selectedOdds.length;
        },
        event(state: BetBuilderState, getters: any, rootState: IState): OfferEvent | null {
            return !state.eventId ? null : rootState.data.sportOffer.events[state.eventId];
        },
        inactiveSelectionsCount(state: BetBuilderState): number {
            if (!state.summary) {
                return 0;
            }

            return getInactiveSelectedOddsCount(state.summary);
        },
        isCreateOddInactive(state: BetBuilderState): boolean {
            if (!state.summary) {
                return false;
            }

            return isOddsSelectionSummaryInactive(state.summary);
        },
        isSelectionInactive(state: BetBuilderState): boolean {
            if (!state.summary) {
                return false;
            }

            return isOddsSelectionSummaryInactive(state.summary) && getInactiveSelectedOddsCount(state.summary) > 0;
        },
        selectedOdds(state: BetBuilderState): Set<BetBuilderOdd['uuid']> {
            return new Set(state.selectedOdds);
        },
        summary(state: BetBuilderState): BetBuilderState['summary'] {
            return state.summary ? {
                ...state.summary,
                legs: state.summary.legs
                    .filter((leg) => state.selectedOdds.includes(leg.uuid))
                    .sort((a, b) => state.selectedOdds.indexOf(a.uuid) - state.selectedOdds.indexOf(b.uuid)),
            } : null;
        },
        unavailableOddUuids(state: BetBuilderState): Set<BetBuilderOdd['uuid']> {
            return new Set(state.unavailableOddUuids);
        },
        isBetCreateLoading(state: BetBuilderState): boolean {
            return state.isBetCreateLoading;
        },
        isBetBuilderAvailable(state: BetBuilderState): boolean {
            return state.isAvailable;
        },
        isBetBuilderLoading(state: BetBuilderState): boolean {
            return state.isBetBuilderLoading;
        },
        markets(state: BetBuilderState): BetBuilderState['markets'] {
            return state.markets;
        },
        oddValueDynamics(state: BetBuilderState): BetBuilderState['oddValueDynamics'] {
            return state.oddValueDynamics;
        },
        isPreviewLoading(state: BetBuilderState): boolean {
            return state.isPreviewLoading;
        },
        isShowingError(state: BetBuilderState): boolean {
            return state.isShowingError;
        },
        // Used for analytics
        summaryDescription(state: BetBuilderState): SummaryDescription | undefined {
            if (state.eventId && state.summary) {
                return {
                    eventId: state.eventId!,
                    summary: state.summary!,
                };
            }
        },
    },
    mutations,
    actions: {
        initializeBetBuilder(context: ActionContext<BetBuilderState, unknown>) {
            featureFlagService.subscribeToFeatureChanges(BetBuilderFeatureFlags.Enabled, (isEnabled) => {
                context.commit(mutationTypes.SET_IS_FEATURE_ENABLED, isEnabled);
            }, false);
            featureFlagService.subscribeToFeatureChanges(BetBuilderFeatureFlags.EventCardLabelEnabled, (isEnabled) => {
                context.commit(mutationTypes.SET_IS_EVENT_CARD_LABEL_ENABLED, isEnabled);
            }, false);
            featureFlagService.subscribeToFeatureChanges(BetBuilderFeatureFlags.OnboardingEnabled, (isEnabled) => {
                context.commit(mutationTypes.SET_IS_ONBOARDING_ENABLED, isEnabled);
            }, false);
            featureFlagService.subscribeToFeatureChanges(BetBuilderFeatureFlags.CrossSellingFromSuperbetsBannerEnabled, (isEnabled) => {
                context.commit(mutationTypes.SET_IS_CROSS_SELLING_FROM_SUPERBETS_BANNER_ENABLED, isEnabled);
            }, false);

            betBuilderService.subscribeToBetBuilderMarketsUpdates((marketsResponse) => {
                const oddValueDynamics = createOddValueDynamics(
                    context.state.markets,
                    marketsResponse.markets,
                    context.state.oddValueDynamics,
                );

                context.commit(mutationTypes.SET_ODD_VALUE_DYNAMICS, oddValueDynamics);
                context.commit(mutationTypes.SET_MARKETS, marketsResponse.markets);
            });

            betBuilderService.subscribeToBetBuilderCustomOddUpdates((data) => {
                context.commit(mutationTypes.SET_SUMMARY, data.summary);
                context.commit(mutationTypes.SET_UNAVAILABLE_ODD_UUIDS, data.unavailableOddsUuids ?? []);
            });

            const getOfferEvent = createOfferEventGetter(() => context.rootGetters['data/sportOffer/eventsMap'], (eventId: OfferEvent['id']) => context.dispatch('data/sportOffer/getEvent', eventId, { root: true }));
            const getOddMarketName = (eventId: number) => context.state.cachedBetslipOddNames[eventId] ?? '';

            betBuilderService.subscribeToBetBuilderCreatedOddUpdates((eventId, odd) => {
                // Implementation detail: We can avoid doing an async call/check here, since we _must_ already
                // have the event in full for this code path to execute at all.
                const event = context.rootGetters['data/sportOffer/eventsMap'][eventId] as OfferEvent;
                if (!event) {
                    logger.error(new Error(`Bet Builder Odd Update: could not find event by ID: ${eventId}`));
                    return;
                }

                const selection = SportSelection.createItem(
                    event,
                    odd,
                    SelectionSourceType.BetBuilder,
                );

                context.dispatch(
                    'ui/sportOffer/betSlip/updateSingleSelection',
                    selection,
                    { root: true },
                );
            }, getOfferEvent, getOddMarketName);

            AuthenticationSubscriptionService.registerPostLoginHandler(() => {
                context.dispatch('_restoreOddUuidsForEvent', {
                    eventId: context.state.eventId,
                    markets: context.state.markets,
                });
            });

            AuthenticationSubscriptionService.registerPostLogoutHandler(() => {
                context.dispatch('clearSelectedOdds', true);
            });
        },
        async fetchBetBuilder(
            { state, rootGetters, commit, dispatch }: ActionContext<BetBuilderState, IState>,
            eventId: number,
        ): Promise<void> {
            try {
                if (!state.isFeatureEnabled) {
                    return;
                }

                commit(mutationTypes.SET_IS_SHOWING_ERROR, false);
                commit(mutationTypes.SET_IS_BET_BUILDER_LOADING, true);

                const response = await betBuilderService.getBetBuilderMarkets(eventId);

                await dispatch('setIsAvailable', response.markets.length > 0);
                await dispatch('setBetBuilderMarkets', response.markets);
                commit(mutationTypes.SET_EVENT_ID, eventId);

                const isLoggedIn = !!rootGetters['data/authentication/user'];

                if (isLoggedIn) {
                    dispatch('_restoreOddUuidsForEvent', {
                        eventId,
                        markets: response.markets,
                    });
                }
            } catch (e) {
                logger.error(e as Error);
            } finally {
                commit(mutationTypes.SET_IS_BET_BUILDER_LOADING, false);
            }
        },
        async createBet(context: ActionContext<BetBuilderState, IState>): Promise<boolean> {
            const { state, commit, dispatch, rootGetters } = context;
            try {
                if (!state.eventId || state.selectedOdds.length === 0 || !state.summary) {
                    await dispatch('clear');
                    return false;
                }
                commit(mutationTypes.SET_IS_BET_CREATE_LOADING, true);
                const sessionId = context.rootGetters['data/authentication/sessionAndUserIds'];
                const event = rootGetters['data/sportOffer/eventsMap'][state.eventId];

                if (!sessionId) {
                    return context.dispatch('ui/modal/setModal', {
                        code: modalCodes.login,
                        afterClose: () => {
                            const hasUser = !!context.rootGetters['data/authentication/user'];

                            if (!hasUser) {
                                dispatch('clearSelectedOdds');
                            }
                        },
                        data: {
                            afterLoginAction: () => {
                                dispatch('createBet');
                            },
                        },
                    }, { root: true });
                }

                const odd = await betBuilderService.createBet(
                    event.id,
                    state.selectedOdds,
                    sessionId,
                    event,
                    getSummaryAsSuperbet(state.summary),
                );
                const selection = SportSelection.createItem(
                    event,
                    odd,
                    // A single odd actually just creates a regular bet
                    state.selectedOdds.length > 1 ? SelectionSourceType.BetBuilder : SelectionSourceType.Regular,
                );

                const result = await dispatch('ui/sportOffer/betSlip/_toggleSelection', { selection }, { root: true });
                dispatch('ui/sportOffer/betSlip/sendBetslipAddResultAnalytic',
                    { selection, isSuccess: result.isSuccess, sourceScreen: SelectionSourceScreen.MatchDetailsOffer }, { root: true },
                );
                if (result.isSuccess) {
                    await dispatch('clearSelectedOdds');
                    commit(mutationTypes.SET_IS_SHOWING_ERROR, false);
                }

                return result.isSuccess;
            } catch (error) {
                logger.error(error as Error);
                // NOTE: Intentionally not awaited, it just wraps commit, we're good.
                // Plus, let's not chain failures by accident.
                dispatch('_showErrorMessage');

                throw error;
            } finally {
                commit(mutationTypes.SET_IS_BET_CREATE_LOADING, false);
            }
        },
        setIsAvailable(context: ActionContext<BetBuilderState, unknown>, isAvailable: boolean) {
            context.commit(mutationTypes.SET_IS_AVAILABLE, isAvailable);
        },
        setBetBuilderMarkets(context: ActionContext<BetBuilderState, unknown>, markets: BetBuilderState['markets']) {
            context.commit(mutationTypes.SET_MARKETS, markets);
        },
        async fetchOddPreview(context: ActionContext<BetBuilderState, unknown>) {
            const sessionId = context.rootGetters['data/authentication/sessionAndUserIds'];

            if (!sessionId) {
                return context.dispatch('ui/modal/setModal', {
                    code: modalCodes.login,
                    afterClose: () => {
                        const hasUser = !!context.rootGetters['data/authentication/user'];

                        if (!hasUser) {
                            context.dispatch('clearSelectedOdds');
                        }
                    },
                    data: {
                        afterLoginAction: () => {
                            context.dispatch('fetchOddPreview');
                        },
                    },
                }, { root: true });
            }

            const { eventId, selectedOdds } = context.state;

            if (!eventId) {
                return;
            }

            try {
                context.commit(mutationTypes.SET_IS_PREVIEW_LOADING, true);

                const getBetBuilderPreviewData = () => betBuilderService.previewBetBuilderCustomOdd(
                    eventId,
                    selectedOdds,
                    sessionId,
                );

                const previewData = await ensureMinimumDurationForSlowProcess(
                    getBetBuilderPreviewData,
                    showLoaderAfterPreviewDurationMs,
                    minimumPreviewLoadingStateDurationMs,
                );

                betBuilderService.liveUpdatePreviewBetBuilderCustomOdd(
                    eventId,
                    selectedOdds,
                    sessionId,
                );

                await context.commit(mutationTypes.SET_SUMMARY, previewData.summary);
                await context.commit(mutationTypes.SET_UNAVAILABLE_ODD_UUIDS, previewData.unavailableOddsUuids ?? []);
                await context.commit(mutationTypes.SET_IS_SHOWING_ERROR, false);
            } catch (error) {
                const latestSummaryLegUuids = new Set(context.state.summary?.legs.map(({ uuid }) => uuid));

                context.commit(mutationTypes.SET_SELECTED_ODDS, selectedOdds.filter((selectedOddUuid) => latestSummaryLegUuids.has(selectedOddUuid)));

                // NOTE: Intentionally not awaited, it just wraps commit, we're good.
                // Plus, let's not chain failures by accident.
                context.dispatch('_showErrorMessage');
            } finally {
                context.commit(mutationTypes.SET_IS_PREVIEW_LOADING, false);
            }
        },
        async getCreatedOdd(
            context: ActionContext<BetBuilderState, IState>,
            payload: GetCreatedOddPayload,
        ): Promise<BetSlipOdd | undefined> {
            const {
                eventId,
                oddUuid,
                event,
                bbOddUuids,
            } = payload;
            const fallbackMarketName = context.state.cachedBetslipOddNames[eventId] ?? '';
            const sessionId = context.rootGetters['data/authentication/sessionAndUserIds'];

            try {
                return await betBuilderService.getCreatedOdd(
                    eventId,
                    oddUuid,
                    event,
                    fallbackMarketName,
                    sessionId,
                    bbOddUuids,
                );
            } catch (error) {
                logger.error(error as Error);
            }
        },
        async toggleSelection(
            context: ActionContext<BetBuilderState, IState>,
            selection: Selection,
        ): Promise<void> {
            const oddUuids: BetBuilderOdd['uuid'][] = toggleSelectionInOdds(context.state.selectedOdds, selection);
            await context.dispatch('_updateOddUuids', oddUuids);
            await context.dispatch('_storeOddHistoryForEvent', {
                eventId: context.state.eventId!,
                oddUuids,
            });
        },
        async clearUnavailableOdds(
            context: ActionContext<BetBuilderState, IState>,
        ): Promise<void> {
            const oddUuids: BetBuilderOdd['uuid'][] = keepActiveOdds(context.state.selectedOdds, context.state.summary?.legs ?? []);
            await context.dispatch('_updateOddUuids', oddUuids);
        },
        clearSelectedOdds({ dispatch, commit, state }: ActionContext<BetBuilderState, IState>, keepHistory = false) {
            // Optimistic UI update to show Betslip faster
            const eventId = state.eventId;

            if (eventId && !keepHistory) {
                betBuilderHistoryService.removeBetBuilderOddUuidsForEvent(String(eventId));
            }

            commit(mutationTypes.SET_SELECTED_ODDS, []);
            commit(mutationTypes.SET_SUMMARY, null);
            commit(mutationTypes.SET_UNAVAILABLE_ODD_UUIDS, []);
            commit(mutationTypes.SET_IS_SHOWING_ERROR, false);
            betBuilderService.unsubscribeFromBetBuilderCustomOddUpdates();

            if (eventId != null) {
                dispatch('fetchBetBuilder', state.eventId);
            }
        },
        clear({ commit }: ActionContext<BetBuilderState, IState>) {
            // Note: Explicitly does NOT clear the history, since this is local
            // state in the scope of the event view, we didn't remove anything
            betBuilderService.unsubscribeFromBetBuilderCustomOddUpdates();
            commit(mutationTypes.CLEAR);
        },
        clearError(context: ActionContext<BetBuilderState, IState>) {
            context.commit(mutationTypes.SET_IS_SHOWING_ERROR, false);
        },
        subscribeToBetslipBetBuilderOddUpdates(
            context: ActionContext<BetBuilderState, IState>,
            payload: BetslipUpdatesPayload,
        ) {
            const sessionId = context.rootGetters['data/authentication/sessionAndUserIds'];
            const eventsMap = context.rootGetters['data/sportOffer/eventsMap'];
            const event = eventsMap[payload.eventId] as OfferEvent;

            betBuilderService.liveUpdateCreateOdd(
                payload.eventId,
                payload.oddUuid,
                event,
                payload.oddMarketName,
                sessionId,
                payload.bbOddUuids,
            );
            context.commit(mutationTypes.SET_CACHED_BETSLIP_ODD_NAME, {
                eventId: payload.eventId,
                name: payload.oddMarketName,
            });
        },
        unsubscribeFromBetslipBetBuilderOddUpdates(
            _context: ActionContext<BetBuilderState, IState>,
            eventId: number,
        ) {
            betBuilderService.unsubscribeFromBetBuilderCreatedOddUpdatesForEvent(eventId);
        },
        async _updateOddUuids(context: ActionContext<BetBuilderState, IState>, oddsUuids: BetBuilderOdd['uuid'][]) {
            // BMB-718: In case of racing update requrests, the selected odds gets out of sync before fetchOddPreview is dispatched

            // BMB-718: By checking the isPreviewLoading state we avoid selected odds overrides of racing update requests
            if (context.state.isPreviewLoading) {
                return;
            }

            // BMB-718: By setting the isPreviewLoading state to TRUE before setting the selected odds we avoid getting unselected unavailable odds
            context.commit(mutationTypes.SET_IS_PREVIEW_LOADING, true);

            context.commit(mutationTypes.SET_SELECTED_ODDS, oddsUuids);
            if (oddsUuids.length > 0 && context.state.eventId) {
                await context.dispatch('fetchOddPreview');
            } else {
                context.commit(mutationTypes.SET_UNAVAILABLE_ODD_UUIDS, []);
                betBuilderService.unsubscribeFromBetBuilderCustomOddUpdates();
            }

            context.commit(mutationTypes.SET_IS_PREVIEW_LOADING, false);
        },
        _storeOddHistoryForEvent(context: ActionContext<BetBuilderState, IState>, payload: HistoryPayload) {
            const eventsMap = context.rootGetters['data/sportOffer/eventsMap'];

            const event = eventsMap[payload.eventId] as OfferEvent;

            if (!event) {
                return;
            }

            betBuilderHistoryService.recordBetBuilderEntryForEvent(
                String(event.id),
                event.date,
                payload.oddUuids,
            );
        },
        _restoreOddUuidsForEvent(context: ActionContext<BetBuilderState, IState>, payload: RestoreHistoryPayload) {
            const oddUuids = betBuilderHistoryService.getBetBuilderOddUuidsForEvent(String(payload.eventId));
            const validOddUuids = oddUuids.filter((uuid) => {
                return payload.markets.some((market) => market.odds.some((odd) => odd.uuid === uuid));
            });

            if (validOddUuids.length) {
                context.dispatch('_updateOddUuids', validOddUuids);
            }
        },
        _showErrorMessage(context: ActionContext<BetBuilderState, IState>) {
            context.commit(mutationTypes.SET_IS_SHOWING_ERROR, true);
        },
    },
};

export type BetBuilderStore = typeof store;

type HistoryPayload = {
    eventId: number;
    oddUuids: BetBuilderOdd['uuid'][];
};

type RestoreHistoryPayload = {
    eventId: number;
    markets: BetBuilderMarket[];
};

type BetslipUpdatesPayload = {
    eventId: number;
    oddUuid: BetSlipOdd['uuid'];
    oddMarketName: string;
    bbOddUuids?: BetBuilderOdd['uuid'][];
};

type SummaryDescription = {
    eventId: number;
    summary: BetBuilderPreviewSummary;
};

type GetCreatedOddPayload = {
    eventId: number;
    oddUuid: BetSlipOdd['uuid'];
    event: OfferEvent;
    bbOddUuids?: BetBuilderOdd['uuid'][];
};
